import { getAuth, signOut } from "firebase/auth";
import { SnapshotIn, types } from "mobx-state-tree";

import { safeLocalStorage } from "helpers/localStorage";
import { logout, LogoutOptions } from "server/actions/logout";
import type { UserOrg } from "types/org";

export interface MeResult {
  id: string;
  name: string;
  email: string;
  phoneNumber?: string | null;
  createdAt: string;
  profileImage?: string | null;
  isAnonymous: boolean;
  orgs: UserOrg[];
}

export enum LoginStatus {
  LoggedIn = "LoggedIn",
  LoggedOut = "LoggedOut",
  Pending = "Pending",
  Error = "Error",
}

const LoggedInUser = types.model("LoggedInUser", {
  id: types.identifier,
  name: types.string,
  email: types.maybe(types.string),
  profileImage: types.maybe(types.string),
  phoneNumber: types.maybe(types.string),
  createdAt: types.string,
  isNew: types.boolean,
});

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
interface ILoggedInUser extends SnapshotIn<typeof LoggedInUser> {}

function getLoggedInUserFromMeQuery(
  me: MeResult,
  isNew: boolean,
): ILoggedInUser | undefined {
  if (me == null) {
    return undefined;
  }
  const user: ILoggedInUser = {
    id: me.id,
    name: me.name,
    email: me.email ?? undefined,
    profileImage: me.profileImage ?? undefined,
    createdAt: me.createdAt,
    isNew,
  };
  return user;
}

export const UserStore = types
  .model("UserStore", {
    status: types.optional(
      types.enumeration("LoginStatus", [
        LoginStatus.LoggedIn,
        LoginStatus.LoggedOut,
        LoginStatus.Pending,
        LoginStatus.Error,
      ]),
      LoginStatus.LoggedOut,
    ),
    user: types.maybe(LoggedInUser),
  })
  .actions((self) => ({
    loggedIn(user: MeResult, isNew: boolean) {
      const u = getLoggedInUserFromMeQuery(user, isNew);
      self.user = LoggedInUser.create(u);
      self.status = LoginStatus.LoggedIn;
    },
    logOut(opts?: LogoutOptions) {
      self.status = LoginStatus.LoggedOut;
      self.user = undefined;
      Promise.all([signOut(getAuth()), logout(opts), clearLocalStorage()]);
    },
    loginError() {
      self.status = LoginStatus.Error;
    },
  }));

const clearLocalStorage = () => {
  safeLocalStorage.clear();
};

export const createUserStore = () => {
  return UserStore.create({});
};
